import { useState, useEffect } from 'react';
import {
    Modal,
    Typography,
    Autocomplete,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Box,
    Snackbar,
    Alert,
    Menu,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    FormControlLabel,
    Paper,
    RadioGroup,
    Radio,
    Divider
} from "@mui/material";
import { v4 as uuidv4 } from 'uuid';
import { useTheme } from '@mui/material/styles';
import TextFieldInput from '../themes/overrides/TextFieldInput';
import useMediaQuery from '@mui/material/useMediaQuery';
import DeleteIcon from '../assets/images/icons/delete_icon.svg';
import ModalBox from "../themes/overrides/ModalBox";
import { useDispatch, useSelector } from "react-redux";
import { putWorkspace } from '../actions/workspaceAction';
import LoadingOverlay from './LoadingOverlay';
import PropTypes from 'prop-types';
import ai_icon from '../assets/images/ai_icon.png'

const ManageAIModal = ({ open, onClose, selectedType, onSuccess }) => {
    const dispatch = useDispatch();
    const [modelAPI, setModelAPI] = useState(null);
    const [selectedAIModel, setSelectedAIModel] = useState(null);
    const [workspaceAIAPIs, setWorkspaceAIAPIs] = useState(null);
    const [generationType, setGenerationType] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const [defaultTextModel, setDefaultTextModel] = useState(null);
    const [defaultImageModel, setDefaultImageModel] = useState(null);

    const textModels = [
        { name: "ChatGPT", value: "chatgpt" },
        // { name: "Claude", value: "claude" },
        { name: "Gemini", value: "gemini" },
        // { name: "Mistral", value: "mistral" },
        // { name: "Llama", value: "llama" },
        { name: "DeepSeek", value: "deepseek" },
        // { name: "Grok", value: "grok" },
        // { name: "Other", value: "other" }
        { name: "Qwen", value: "qwen" },
    ];

    const imageModels = [
        { name: "Vyro", value: "vyro" },
    ]

    const currentWorkspace = useSelector((state) => state.workspace.workspaceInfo);

    useEffect(() => {
        // fetchlocaleFunction()
        if (selectedType) {
            setWorkspaceAIAPIs(currentWorkspace?.data?.aiModels)

            // Initialize default models from workspace
            setDefaultTextModel(currentWorkspace.data.defaultAiModel?.text || null);
            setDefaultImageModel(currentWorkspace.data.defaultAiModel?.image || null);

        }
    }, [currentWorkspace, selectedType]);


    const handleAPIChange = (event) => {
        setModelAPI(event.target.value);
    };

    const handleGenerationTypeChange = (event) => {
        setGenerationType(event);
        setSelectedAIModel('');
    }

    const handleRemoveAPI = async (modelAPIToRemove) => {
        setLoading(true);
        try {
            const workspaceId = selectedType._id;
            const type = 'removeAiModels';
            const selectedmodelAPIToRemove = [modelAPIToRemove.id];

            // Remove the model
            await dispatch(putWorkspace(workspaceId, selectedmodelAPIToRemove, type));

            // Determine the next default
            let nextDefaultId = null; // default to null
            if (modelAPIToRemove.type === 'text') {
                const remainingTextModels = workspaceAIAPIs?.filter(m => m.type === 'text' && m.id !== modelAPIToRemove.id);
                nextDefaultId = remainingTextModels?.[0]?.id || null; // use null if none remain
                await handleSetDefaultModel(nextDefaultId, 'text');
            } else if (modelAPIToRemove.type === 'image') {
                const remainingImageModels = workspaceAIAPIs?.filter(m => m.type === 'image' && m.id !== modelAPIToRemove.id);
                nextDefaultId = remainingImageModels?.[0]?.id || null; // use null if none remain
                await handleSetDefaultModel(nextDefaultId, 'image');
            }

            onSuccess?.();
            showSnackbar('API removed successfully', 'success');
        } catch (error) {
            showSnackbar('Failed to remove API', 'error');
            console.error('Error removing API:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleSaveModelAPI = async () => {
        setLoading(true);
        try {
            const workspaceId = selectedType._id;
            const type = 'newAiModels';
            const AIAPIs =
            {
                id: uuidv4(),
                model: selectedAIModel,
                type: generationType,
                apiKey: modelAPI
            }

            await dispatch(putWorkspace(workspaceId, AIAPIs, type))
                .then(async () => {
                    // If this is the first model of its type, set it as default
                    const modelType = AIAPIs.type;
                    const currentTypeModels = workspaceAIAPIs?.filter(m => m.type === modelType) || [];
                    if (currentTypeModels.length === 0) {
                        await handleSetDefaultModel(AIAPIs.id, modelType);
                    }
                    onSuccess?.();
                })
            showSnackbar('API updated successfully', 'success');
            setSelectedAIModel('')
            setModelAPI('')
        } catch (error) {
            showSnackbar('Failed to add API', 'error');
            console.error('Error saving API:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleSetDefaultModel = async (modelId, type) => {
        setLoading(true);
        try {
            const workspaceId = selectedType._id;

            // Prepare the updated defaults
            const updatedDefaults = {
                text: type === 'text' ? modelId : defaultTextModel,
                image: type === 'image' ? modelId : defaultImageModel,
            };

            // Dispatch full object
            await dispatch(putWorkspace(workspaceId, updatedDefaults, 'defaultAiModel'));

            showSnackbar('Default model updated successfully', 'success');

            // Update local state
            if (type === 'text') setDefaultTextModel(modelId);
            else if (type === 'image') setDefaultImageModel(modelId);

            onSuccess?.();
        } catch (error) {
            showSnackbar('Failed to update default model', 'error');
            console.error('Error setting default model:', error);
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalBox sx={{ width: '700px', maxWidth: '95%' }}>
                <Typography variant="h2" sx={{ mb: 3 }}>Manage AI APIs</Typography>

                {/* Generation Type Selection */}
                <RadioGroup
                    row
                    value={generationType}
                    onChange={(event) => handleGenerationTypeChange(event.target.value)}
                    sx={{ width: '100%', mb: 2, gap: isMobile ? 2 : 0 }}
                >
                    {[
                        { type: "text", label: "Text Generation", description: "Generate text for blogs, content, or summaries." },
                        { type: "image", label: "Image Generation", description: "Create AI-generated images based on text descriptions." }
                    ].map(({ type, label, description }) => (
                        <FormControlLabel
                            key={type}
                            value={type}
                            sx={{
                                border: `1px solid ${generationType === type ? "rgba(36,93,173,1)" : "#E6E6E6"}`,
                                borderRadius: 3,
                                width: isMobile ? '100%' : '47%',
                                p: 2,
                                transition: "0.3s",
                                "&:hover": {
                                    borderColor: "rgba(36,93,173,0.8)"
                                },
                                marginLeft: 0
                            }}
                            control={<Radio sx={{ display: "none" }} />}
                            label={
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <IconButton onClick={() => handleGenerationTypeChange(type)}
                                            color={generationType === type ? "primary" : "default"}
                                        >
                                            <img src={ai_icon} alt={`${label} Icon`} style={{ width: 28, height: 28 }} />
                                        </IconButton>
                                        <Typography fontSize={16} fontWeight={600} sx={{ color: generationType === type ? "rgba(36,93,173,1)" : "#6B6B6B", ml: 1 }}>
                                            {label}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ my: 1, width: "100%" }} color={generationType === type ? "rgba(36,93,173,1)" : "#ffffff"} />
                                    <Typography sx={{ fontSize: '14px', textAlign: 'center', color: generationType === type ? "#003638" : "#6B6B6B" }}>
                                        {description}
                                    </Typography>
                                </Box>
                            }
                            labelPlacement="bottom"
                        />
                    ))}
                </RadioGroup>


                {generationType && (
                    <>
                        <Box sx={{ display: isMobile ? 'block' : 'flex', gap: 2, mb: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel id="ai-model-label">Select AI Model</InputLabel>
                                <Select
                                    labelId="ai-model-label"
                                    value={selectedAIModel || ''}
                                    onChange={(event) => setSelectedAIModel(event.target.value)}
                                    label="Select AI Model"
                                    sx={{
                                        fontSize: "1.1rem",
                                        color: "#333",
                                    }}
                                >
                                    {generationType === 'text' ? (
                                        textModels.map((model) => (
                                            <MenuItem sx={{
                                                fontSize: "1rem",
                                            }}
                                                value={model.value}>{model.name}</MenuItem>
                                        ))
                                    ) : (
                                        imageModels.map((model) => (
                                            <MenuItem sx={{
                                                fontSize: "1rem",
                                            }}
                                                value={model.value}>{model.name}</MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ display: isMobile ? 'block' : 'flex', gap: 2, mb: 3 }}>
                            <TextFieldInput
                                label="API Key"
                                variant="outlined"
                                fullWidth
                                onChange={handleAPIChange}
                                value={modelAPI}
                                // error={Boolean(error)}
                                // helperText={error}
                                sx={{ marginBottom: 2 }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSaveModelAPI}
                                disabled={!selectedAIModel || !modelAPI || loading}
                                sx={{ width: isMobile ? '100%' : '120px', mt: isMobile ? 2 : 0, height: 45 }}
                            >
                                Add API
                            </Button>
                        </Box>
                    </>
                )}
                <List>
                    <Typography variant="h6" sx={{ mb: 2 }}>Workspace AI APIs</Typography>

                    {workspaceAIAPIs?.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 4 }}>
                            {/* Text Models Column */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    Text Models
                                </Typography>

                                {(() => {
                                    const textModels = workspaceAIAPIs
                                        .filter(api => api.type === 'text')
                                        .sort((a, b) => (a.id === defaultTextModel ? -1 : b.id === defaultTextModel ? 1 : 0));

                                    if (textModels.length === 0) {
                                        return <Typography variant="body2">No text models available</Typography>;
                                    }

                                    return textModels.map(workspaceAIAPI => (
                                        <ListItem
                                            key={workspaceAIAPI.id}
                                            secondaryAction={
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {defaultTextModel === workspaceAIAPI.id ? (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Default
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleSetDefaultModel(workspaceAIAPI.id, 'text')}
                                                        >
                                                            Set Default
                                                        </Button>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#FF4842' }}
                                                        onClick={() => handleRemoveAPI(workspaceAIAPI)}
                                                    >
                                                        <img src={DeleteIcon} alt="delete" />
                                                    </IconButton>
                                                </Box>
                                            }
                                        >
                                            <ListItemText primary={workspaceAIAPI.model.charAt(0).toUpperCase() + workspaceAIAPI.model.slice(1)} />
                                        </ListItem>
                                    ));
                                })()}
                            </Box>

                            {/* Image Models Column */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    Image Models
                                </Typography>

                                {(() => {
                                    const imageModels = workspaceAIAPIs
                                        .filter(api => api.type === 'image')
                                        .sort((a, b) => (a.id === defaultImageModel ? -1 : b.id === defaultImageModel ? 1 : 0));

                                    if (imageModels.length === 0) {
                                        return <Typography variant="body2">No image models available</Typography>;
                                    }

                                    return imageModels.map(workspaceAIAPI => (
                                        <ListItem
                                            key={workspaceAIAPI.id}
                                            secondaryAction={
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {defaultImageModel === workspaceAIAPI.id ? (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Default
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleSetDefaultModel(workspaceAIAPI.id, 'image')}
                                                        >
                                                            Set Default
                                                        </Button>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#FF4842' }}
                                                        onClick={() => handleRemoveAPI(workspaceAIAPI)}
                                                    >
                                                        <img src={DeleteIcon} alt="delete" />
                                                    </IconButton>
                                                </Box>
                                            }
                                        >
                                            <ListItemText primary={workspaceAIAPI.model.charAt(0).toUpperCase() + workspaceAIAPI.model.slice(1)} />
                                        </ListItem>
                                    ));
                                })()}
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="subtitle1" sx={{ color: '#1a1a1a' }}>
                                No saved Models found
                            </Typography>
                        </Box>
                    )}
                </List>
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setOpenSnackbar(false)}
                        severity={snackbarSeverity}
                        sx={{ width: 380 }}
                    >{snackbarMessage}</Alert>

                </Snackbar>
                {loading && <LoadingOverlay />}
            </ModalBox >
        </Modal >
    );
};

ManageAIModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    selectedType: PropTypes.any,
    onSuccess: PropTypes.func
};

export default ManageAIModal;